import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter, MapPin, Briefcase, Star, Diamond } from 'lucide-react';
import { getFirstName } from '../utils/nameUtils';

const BrowseMembersPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    gender: '',
    minAge: '',
    maxAge: '',
    membershipTier: '',
    incomeVerified: '',
    maxDistance: ''
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/members/browse`);
      const data = await response.json();
      setMembers(data);
      setFilteredMembers(data);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...members];

    if (filters.gender) {
      filtered = filtered.filter(m => m.sex === filters.gender);
    }
    if (filters.minAge) {
      filtered = filtered.filter(m => m.age >= parseInt(filters.minAge));
    }
    if (filters.maxAge) {
      filtered = filtered.filter(m => m.age <= parseInt(filters.maxAge));
    }
    if (filters.membershipTier) {
      filtered = filtered.filter(m => m.membership_tier === filters.membershipTier);
    }
    if (filters.incomeVerified) {
      filtered = filtered.filter(m => m.income_verified === filters.incomeVerified);
    }
    if (filters.maxDistance) {
      filtered = filtered.filter(m => m.distance <= parseFloat(filters.maxDistance));
    }

    setFilteredMembers(filtered);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      gender: '',
      minAge: '',
      maxAge: '',
      membershipTier: '',
      incomeVerified: '',
      maxDistance: ''
    });
    setFilteredMembers(members);
  };

  const renderIncomeBadge = (incomeLevel) => {
    if (!incomeLevel) return null;
    
    // Map legacy 'silver' to 'gold' for backwards compatibility
    const level = incomeLevel === 'silver' ? 'gold' : incomeLevel;

    const badges = {
      gold: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 shadow-lg" title="Gold - $150k-$500k">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
            <circle cx="12" cy="12" r="10" fill="url(#goldGrad)" stroke="#B8860B" strokeWidth="0.5"/>
            <circle cx="12" cy="12" r="7" fill="url(#goldInner)" />
            <defs>
              <radialGradient id="goldGrad">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="100%" stopColor="#DAA520" />
              </radialGradient>
              <radialGradient id="goldInner">
                <stop offset="0%" stopColor="#FFF8DC" />
                <stop offset="100%" stopColor="#F0E68C" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      ),
      diamond: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-blue-300 via-blue-400 to-blue-500 shadow-lg" title="Diamond - $500k+">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
            <path d="M12 2 L16 8 L22 10 L16 16 L12 22 L8 16 L2 10 L8 8 Z" fill="url(#diamondGrad)" stroke="#1E90FF" strokeWidth="0.5"/>
            <path d="M12 6 L14 10 L18 11 L14 14 L12 18 L10 14 L6 11 L10 10 Z" fill="url(#diamondInner)" />
            <defs>
              <linearGradient id="diamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E0F7FF" />
                <stop offset="50%" stopColor="#87CEEB" />
                <stop offset="100%" stopColor="#4682B4" />
              </linearGradient>
              <radialGradient id="diamondInner">
                <stop offset="0%" stopColor="#F0FFFF" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#B0E0E6" stopOpacity="0.6" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      ),
      blue_diamond: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-blue-300 via-blue-400 to-blue-500 shadow-lg" title="Blue Diamond - $500k+">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
            <path d="M12 2 L16 8 L22 10 L16 16 L12 22 L8 16 L2 10 L8 8 Z" fill="url(#diamondGrad)" stroke="#1E90FF" strokeWidth="0.5"/>
            <path d="M12 6 L14 10 L18 11 L14 14 L12 18 L10 14 L6 11 L10 10 Z" fill="url(#diamondInner)" />
            <defs>
              <linearGradient id="diamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E0F7FF" />
                <stop offset="50%" stopColor="#87CEEB" />
                <stop offset="100%" stopColor="#4682B4" />
              </linearGradient>
              <radialGradient id="diamondInner">
                <stop offset="0%" stopColor="#F0FFFF" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#B0E0E6" stopOpacity="0.6" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      ),
      green_diamond: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-emerald-300 via-emerald-400 to-emerald-500 shadow-lg" title="Green Diamond">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
            <path d="M12 2 L16 8 L22 10 L16 16 L12 22 L8 16 L2 10 L8 8 Z" fill="url(#greenDiamondGrad)" stroke="#059669" strokeWidth="0.5"/>
            <path d="M12 6 L14 10 L18 11 L14 14 L12 18 L10 14 L6 11 L10 10 Z" fill="url(#greenDiamondInner)" />
            <defs>
              <linearGradient id="greenDiamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A7F3D0" />
                <stop offset="50%" stopColor="#6EE7B7" />
                <stop offset="100%" stopColor="#10B981" />
              </linearGradient>
              <radialGradient id="greenDiamondInner">
                <stop offset="0%" stopColor="#D1FAE5" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#34D399" stopOpacity="0.6" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      ),
      black_diamond: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-gray-700 via-gray-800 to-black shadow-lg" title="Black Diamond - Elite">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="drop-shadow-md">
            <path d="M12 2 L16 8 L22 10 L16 16 L12 22 L8 16 L2 10 L8 8 Z" fill="url(#blackDiamondGrad)" stroke="#1F2937" strokeWidth="0.5"/>
            <path d="M12 6 L14 10 L18 11 L14 14 L12 18 L10 14 L6 11 L10 10 Z" fill="url(#blackDiamondInner)" />
            <defs>
              <linearGradient id="blackDiamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4B5563" />
                <stop offset="50%" stopColor="#1F2937" />
                <stop offset="100%" stopColor="#000000" />
              </linearGradient>
              <radialGradient id="blackDiamondInner">
                <stop offset="0%" stopColor="#6B7280" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#111827" stopOpacity="0.6" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      )
    };

    return badges[level] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black pb-24">
      {/* Header */}
      <div className="glass-panel-luxury sticky top-0 z-10 border-b border-gold/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="gold-button-3d p-2 rounded-lg"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-cinzel gold-text-luxury">Browse Members</h1>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="gold-button-3d p-2 rounded-lg relative"
          >
            <Filter size={24} />
            {Object.values(filters).some(v => v) && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-burgundy rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="glass-panel-luxury p-6">
            <h3 className="text-xl font-cinzel gold-text-luxury mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gold mb-2 text-sm">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                >
                  <option value="">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-gold mb-2 text-sm">Min Age</label>
                <input
                  type="number"
                  value={filters.minAge}
                  onChange={(e) => setFilters({ ...filters, minAge: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                  placeholder="21"
                />
              </div>
              <div>
                <label className="block text-gold mb-2 text-sm">Max Age</label>
                <input
                  type="number"
                  value={filters.maxAge}
                  onChange={(e) => setFilters({ ...filters, maxAge: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                  placeholder="99"
                />
              </div>
              <div>
                <label className="block text-gold mb-2 text-sm">Membership</label>
                <select
                  value={filters.membershipTier}
                  onChange={(e) => setFilters({ ...filters, membershipTier: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                >
                  <option value="">All</option>
                  <option value="elite">Elite</option>
                  <option value="basic">Basic</option>
                </select>
              </div>
              <div>
                <label className="block text-gold mb-2 text-sm">Income Verified</label>
                <select
                  value={filters.incomeVerified}
                  onChange={(e) => setFilters({ ...filters, incomeVerified: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                >
                  <option value="">All</option>
                  <option value="gold">Gold</option>
                  <option value="diamond">Diamond</option>
                </select>
              </div>
              <div>
                <label className="block text-gold mb-2 text-sm">Max Distance (mi)</label>
                <input
                  type="number"
                  step="0.1"
                  value={filters.maxDistance}
                  onChange={(e) => setFilters({ ...filters, maxDistance: e.target.value })}
                  className="w-full bg-black/40 border border-gold/20 rounded-lg px-3 py-2 text-cream text-sm"
                  placeholder="10"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={applyFilters}
                className="flex-1 gold-button-3d py-2 rounded-lg"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="px-6 py-2 rounded-lg border border-gold/30 text-gold hover:bg-gold/10 transition-all"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <p className="text-cream/60 mb-4">{filteredMembers.length} members found</p>
        
        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-gold/20 border-t-gold rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="glass-panel-luxury p-12 text-center">
            <p className="text-cream/70 text-lg">No members found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => navigate(`/user/${member.id}`)}
                className="glass-panel-luxury overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
              >
                {/* Photo */}
                <div className="relative aspect-[3/4]">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  {member.online && (
                    <div className="absolute top-3 right-3 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-cormorant gold-text-luxury">{getFirstName(member.name)}</h3>
                    {member.age && <span className="text-cream/70">{member.age}</span>}
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-2 mb-3">
                    {member.membership_tier === 'elite' && (
                      <span className="px-2 py-1 bg-gradient-to-r from-gold via-gold-deep to-gold rounded-full text-xs text-black font-semibold flex items-center gap-1">
                        <Star size={12} fill="currentColor" /> ELITE
                      </span>
                    )}
                    {member.verified && (
                      <span className="px-2 py-1 bg-blue-500/20 border border-blue-500/40 rounded-full text-xs text-blue-400">✓ Verified</span>
                    )}
                    {renderIncomeBadge(member.income_verified)}
                  </div>

                  {member.occupation && (
                    <p className="text-cream/70 text-sm flex items-center gap-2 mb-2">
                      <Briefcase size={14} />
                      {member.occupation}
                    </p>
                  )}
                  {member.location && (
                    <p className="text-cream/70 text-sm flex items-center gap-2">
                      <MapPin size={14} />
                      {member.distance}mi • {member.location}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseMembersPage;